import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { getCurrentMode } from '@/lib/mode/setCurrentMode';

/**
 * Shared axios instance used by orval-generated hooks and both the admin and
 * portal auth flows.
 *
 * - `baseURL: "/api"` matches Vite's dev proxy (in prod the SPA is served from
 *   the same origin as the API).
 * - `withCredentials: true` so the `pgp_admin_refresh` / `pgp_client_refresh`
 *   httpOnly cookies travel with `/auth/admin/*` and `/auth/portal/*` requests.
 * - A single 401 interceptor picks the correct refresh handler by URL prefix
 *   (`/portal/*` + `/auth/portal/*` → portal, everything else → admin).
 */
export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

type RefreshHandler = () => Promise<string | null>;
type LogoutHandler = () => void;

let adminAccessToken: string | null = null;
let portalAccessToken: string | null = null;

let adminRefreshHandler: RefreshHandler | null = null;
let adminLogoutHandler: LogoutHandler | null = null;

let portalRefreshHandler: RefreshHandler | null = null;
let portalLogoutHandler: LogoutHandler | null = null;

export function setAdminAccessToken(token: string | null): void {
  adminAccessToken = token;
}

export function setPortalAccessToken(token: string | null): void {
  portalAccessToken = token;
}

export function registerAuthHandlers(handlers: {
  refresh: RefreshHandler;
  logout: LogoutHandler;
}): void {
  adminRefreshHandler = handlers.refresh;
  adminLogoutHandler = handlers.logout;
}

export function registerPortalAuthHandlers(handlers: {
  refresh: RefreshHandler;
  logout: LogoutHandler;
}): void {
  portalRefreshHandler = handlers.refresh;
  portalLogoutHandler = handlers.logout;
}

function isPortalUrl(url?: string | null): boolean {
  if (!url) return false;
  return url.startsWith('/portal') || url.startsWith('/auth/portal');
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.headers?.Authorization) {
    const token = isPortalUrl(config.url) ? portalAccessToken : adminAccessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  // Stamp the current operation mode on every outbound request. Backend may
  // ignore the header today (Phase B will honor it); we ship the contract
  // now so the wire shape is stable when the API catches up.
  if (config.headers && !config.headers['X-DWPay-Mode']) {
    config.headers['X-DWPay-Mode'] = getCurrentMode();
  }
  return config;
});

interface RetryableConfig extends InternalAxiosRequestConfig {
  _pgpRetry?: boolean;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    if (!original || error.response?.status !== 401 || original._pgpRetry) {
      throw error;
    }

    const portal = isPortalUrl(original.url);

    // Don't try to refresh from within the refresh/login endpoints themselves.
    if (portal) {
      if (original.url?.includes('/auth/portal/refresh') || original.url?.includes('/auth/portal/login')) {
        throw error;
      }
      if (!portalRefreshHandler) throw error;

      original._pgpRetry = true;
      const fresh = await portalRefreshHandler();
      if (fresh) {
        original.headers.Authorization = `Bearer ${fresh}`;
        return http.request(original);
      }
      portalLogoutHandler?.();
    } else {
      if (original.url?.includes('/auth/admin/refresh') || original.url?.includes('/auth/admin/login')) {
        throw error;
      }
      if (!adminRefreshHandler) throw error;

      original._pgpRetry = true;
      const fresh = await adminRefreshHandler();
      if (fresh) {
        original.headers.Authorization = `Bearer ${fresh}`;
        return http.request(original);
      }
      adminLogoutHandler?.();
    }

    throw error;
  },
);

export const apiFetcher = async <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const response = await http.request<T>({ ...config, ...options });
  return response.data;
};

export default apiFetcher;
